<script lang="ts">
  import ModalShell from './ModalShell.svelte'
  import { buildShareUrl, SHARE_MAX_BYTES } from '../lib/share'
  import { showToast } from '../state/toast.svelte'

  interface Props {
    source: string
    onClose: () => void
  }
  let { source, onClose }: Props = $props()

  let url = $state<string | null>(null)
  let bytes = $state(0)

  $effect(() => {
    bytes = new TextEncoder().encode(source).length
    url = buildShareUrl(source)
  })

  function copy(): void {
    if (!url) return
    void navigator.clipboard.writeText(url).then(() => showToast('Link copied'))
  }
</script>

<ModalShell labelledBy="share-title" onClose={onClose}>
  <div class="dialog">
    <header>
      <h2 id="share-title">Share link</h2>
      <button type="button" class="close" onclick={onClose}>Close</button>
    </header>
    {#if url}
      <p class="hint">Anyone with this link can view the document.</p>
      <input class="url" readonly value={url} onclick={(e) => (e.target as HTMLInputElement).select()} />
      <p class="meta">{bytes.toLocaleString()} / {SHARE_MAX_BYTES.toLocaleString()} bytes</p>
      <div class="actions">
        <button type="button" class="primary" onclick={copy}>Copy link</button>
        <button type="button" onclick={onClose}>Done</button>
      </div>
    {:else}
      <p class="error">Document is too large to share ({bytes.toLocaleString()} bytes). Try exporting as HTML instead.</p>
      <div class="actions">
        <button type="button" onclick={onClose}>Close</button>
      </div>
    {/if}
  </div>
</ModalShell>

<style>
  .dialog {
    padding: var(--space-5);
    width: min(480px, 92vw);
  }
  header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--space-4);
  }
  h2 {
    margin: 0;
    font-size: var(--text-lg);
    font-weight: 600;
  }
  .close {
    border: none;
    background: none;
    font: inherit;
    color: var(--text-secondary);
    cursor: pointer;
  }
  .hint {
    margin: 0 0 var(--space-3);
    font-size: var(--text-sm);
    color: var(--text-secondary);
  }
  .url {
    width: 100%;
    padding: 10px;
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-sm);
    font-family: var(--font-mono);
    font-size: var(--text-sm);
    background: var(--bg-page);
    color: var(--text-primary);
  }
  .meta {
    margin: var(--space-2) 0 var(--space-4);
    font-size: var(--text-xs);
    color: var(--text-muted);
  }
  .actions {
    display: flex;
    gap: var(--space-2);
  }
  /* Default = secondary button. The Done/Close fell back to the
     user-agent default text color before — `font: inherit` does not carry
     `color`, only typography metrics — so on dark mode it rendered as
     near-black text on a near-black surface and was effectively invisible.
     Setting `color: var(--text-primary)` explicitly + a hover state
     restores parity with ConfirmDialog.cancel. */
  .actions button {
    padding: 8px 14px;
    border-radius: var(--radius-sm);
    border: 1px solid var(--border-subtle);
    background: var(--bg-page);
    color: var(--text-primary);
    font: inherit;
    font-size: var(--text-sm);
    font-weight: 500;
    cursor: pointer;
    transition: background var(--motion-fast), border-color var(--motion-fast);
  }
  .actions button:hover {
    background: var(--bg-sunken);
    border-color: var(--border-strong);
  }
  .actions button:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
  .primary {
    background: var(--accent) !important;
    border-color: var(--accent) !important;
    color: var(--accent-contrast) !important;
  }
  .primary:hover {
    filter: brightness(1.05);
  }
  .error {
    color: var(--callout-caution);
    font-size: var(--text-sm);
    margin-bottom: var(--space-3);
  }
</style>
